import { PlatformRole, type MeDto, type RegisterRequestDto, type UpdatePreferencesRequestDto } from '@gr/core/accounts/identity/dto';
import type { Locale } from '@gr/core/platform/user-preferences/locales';
import type { ColorMode, ThemeName } from '@gr/core/platform/user-preferences/themes';
import type { BlogRole, MyBlogDto } from '@gr/core/accounts/workspaces/dto';
import { roleHasPermission, type BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { ResultHelper, type OperationResult } from '@gr/utils/operation-result';
import type { I18nService } from '../../../shared/i18n/i18n';
import { Observable } from '../../../shared/store';
import type { ThemeService } from '../../../shared/theme/theme-service';
import type { IdentityApi } from './identity.api';

export enum SessionStatus {
  Loading = 'loading',
  Anonymous = 'anonymous',
  Authenticated = 'authenticated',
}

export interface SessionState {
  status: SessionStatus;
  me: MeDto | null;
}

/**
 * Текущий пользователь (IAM-UC-3), его блоги и права (WS-UC-6). Настройки
 * интерфейса синхронизируются: до входа — браузер, после входа — профиль
 * (PREF-UC-1, PREF-UC-2).
 */
export class SessionService {
  readonly state = new Observable<SessionState>({ status: SessionStatus.Loading, me: null });

  constructor(
    private readonly api: IdentityApi,
    private readonly i18n: I18nService,
    private readonly theme: ThemeService,
  ) {}

  get me(): MeDto | null {
    return this.state.get().me;
  }

  async load(): Promise<void> {
    const result = await this.api.me();
    this.applyMe(result.success ? result.data : null);
  }

  // IAM-UC-1
  async login(login: string, password: string): Promise<OperationResult<MeDto | null>> {
    const result = await this.api.login(login, password);
    if (!result.success) return ResultHelper.Forward(result, null);
    const me = await this.api.me();
    if (!me.success) return ResultHelper.Forward(me, null);
    this.applyMe(me.data);
    return ResultHelper.Ok(me.data);
  }

  // IAM-UC-14, IAM-UC-14.CC-4: регистрация сразу открывает сессию — как вход.
  async register(input: RegisterRequestDto): Promise<OperationResult<MeDto | null>> {
    const result = await this.api.register(input);
    if (!result.success) return ResultHelper.Forward(result, null);
    const me = await this.api.me();
    if (!me.success) return ResultHelper.Forward(me, null);
    this.applyMe(me.data);
    return ResultHelper.Ok(me.data);
  }

  // IAM-UC-2
  async logout(): Promise<void> {
    await this.api.logout();
    this.applyMe(null);
  }

  /** IAM-UC-3.CC-1: сессия истекла — состояние сбрасывается без запроса к серверу. */
  expire(): void {
    if (this.state.get().status === SessionStatus.Authenticated) this.applyMe(null);
  }

  async refresh(): Promise<void> {
    const result = await this.api.me();
    if (result.success) this.applyMe(result.data);
  }

  async setLocale(locale: Locale): Promise<void> {
    this.i18n.setLocale(locale);
    await this.savePreferences({ locale });
  }

  async setTheme(theme: ThemeName): Promise<void> {
    this.theme.setTheme(theme);
    await this.savePreferences({ theme });
  }

  async setColorMode(colorMode: ColorMode): Promise<void> {
    this.theme.setMode(colorMode);
    await this.savePreferences({ colorMode });
  }

  // WS-UC-2: последний выбранный блог.
  async rememberBlog(blogId: string): Promise<void> {
    const me = this.me;
    if (!me || me.preferences.lastBlogId === blogId) return;
    await this.savePreferences({ lastBlogId: blogId });
  }

  blogs(): MyBlogDto[] {
    return this.me?.blogs ?? [];
  }

  roleIn(blogId: string): BlogRole | null {
    return this.blogs().find((blog) => blog.id === blogId)?.role ?? null;
  }

  can(blogId: string, permission: BlogPermission): boolean {
    const role = this.roleIn(blogId);
    return role !== null && roleHasPermission(role, permission);
  }

  isAdmin(): boolean {
    return this.me?.user.platformRole === PlatformRole.Admin;
  }

  private async savePreferences(input: UpdatePreferencesRequestDto): Promise<void> {
    if (this.state.get().status !== SessionStatus.Authenticated) return;
    const result = await this.api.updatePreferences(input);
    if (result.success) this.state.set({ status: SessionStatus.Authenticated, me: result.data });
  }

  private applyMe(me: MeDto | null): void {
    if (me) {
      this.i18n.setLocale(me.preferences.locale);
      this.theme.setTheme(me.preferences.theme);
      this.theme.setMode(me.preferences.colorMode);
    }
    this.state.set({ status: me ? SessionStatus.Authenticated : SessionStatus.Anonymous, me });
  }
}
